/**
 * CR-6451: the NCR filter panel's state shape, its empty value, and the
 * mapping from panel state to the NCR list's query params.
 *
 * Kept as pure data + one pure function so the page, the chip counts and a
 * unit test all read the same mapping — the class of bug the ratchet log
 * records twice is a control that "looks right and filters nothing" because
 * its key never reached the wire.
 */
import { NO_SUPPLIER_FILTER_VALUE } from './ncrNoSupplier';

export interface NCRFiltersState {
  searchTerm: string;
  statusFilter: string;
  dateFrom: string;
  dateTo: string;
  // CR-6444: Division filter for Division → Site cascade
  divisionFilter: string;
  /** CR-6444: Company Country filter in Company section. */
  companyCountryFilter: string;
  siteFilter: string;
  supplierGroupFilter: string;
  supplierFilter: string;
  /** CR-6444: Supplier Code text filter. */
  supplierCodeFilter: string;
  /** CR-7350: Quality Engineer, contains-match on the column's own
   *  resolution (supplier profile, then the legacy company field). Owned by
   *  the panel so it gets a chip and is cleared by Clear all — the column
   *  funnel writes the same state. */
  qualityEngineerFilter: string;
  /** CR-7350: Support Quality Engineer (CR-7345), same contains-match. */
  supportQualityEngineerFilter: string;
  commodityGroupFilter: string;
  commodityTypeFilter: string;
  /** CR-6444: Supplier Type filter in Classification section. */
  supplierTypeFilter: string;
  strategicFilter: boolean;
  // Priority-1 NCR list filters.
  partNumberFilter: string;
  regionFilter: string;
  liabilityFilter: string;
  warehouseFilter: string;
  claimStatusFilter: string;
  actionStatusFilter: string;
  collectionDateFrom: string;
  collectionDateTo: string;
  // CR-6451: supplier-panel controls from the design.
  supplierCountryFilter: string;
  /** Site NCR / SRN No. — contains-match on the NCR's search log number. */
  srnFilter: string;
  /** NCR ID — contains-match on ncr_number. */
  ncrIdFilter: string;
  creditRequired: boolean;
  eightDRequired: boolean;
  collectionRequired: boolean;
  replacementRequired: boolean;
}

export const EMPTY_NCR_FILTERS: NCRFiltersState = {
  searchTerm: '',
  statusFilter: '',
  dateFrom: '',
  dateTo: '',
  divisionFilter: '',
  companyCountryFilter: '',
  siteFilter: '',
  supplierGroupFilter: '',
  supplierFilter: '',
  supplierCodeFilter: '',
  qualityEngineerFilter: '',
  supportQualityEngineerFilter: '',
  commodityGroupFilter: '',
  commodityTypeFilter: '',
  supplierTypeFilter: '',
  strategicFilter: false,
  partNumberFilter: '',
  regionFilter: '',
  liabilityFilter: '',
  warehouseFilter: '',
  claimStatusFilter: '',
  actionStatusFilter: '',
  collectionDateFrom: '',
  collectionDateTo: '',
  supplierCountryFilter: '',
  srnFilter: '',
  ncrIdFilter: '',
  creditRequired: false,
  eightDRequired: false,
  collectionRequired: false,
  replacementRequired: false,
};

/**
 * Query params for the panel-owned filters (the page adds its own: search,
 * reject / next_action / archived buckets, PO deep-link, view context).
 * Empty values are omitted.
 *
 * @param f - the applied panel state
 * @returns the NCR list params, ready to spread into the request
 */
export function buildNCRPanelParams(f: NCRFiltersState): Record<string, string | boolean | undefined> {
  return {
    // 'Open' is the spec's top-level state (every issued, not-closed NCR),
    // not a status value, so it travels as ncr_state.
    status: f.statusFilter && f.statusFilter !== 'open' ? f.statusFilter : undefined,
    ncr_state: f.statusFilter === 'open' ? 'open' : undefined,
    date_from: f.dateFrom || undefined,
    date_to: f.dateTo || undefined,
    // CR-6444: division and company country filters
    division: f.divisionFilter || undefined,
    'site__country': f.companyCountryFilter || undefined,
    site: f.siteFilter || undefined,
    'supplier__group': f.supplierGroupFilter || undefined,
    // quality.no_supplier: the customer panel's "No supplier (internal)"
    // option is a sentinel, not a supplier id — it travels as no_supplier.
    supplier: f.supplierFilter && f.supplierFilter !== NO_SUPPLIER_FILTER_VALUE ? f.supplierFilter : undefined,
    no_supplier: f.supplierFilter === NO_SUPPLIER_FILTER_VALUE ? true : undefined,
    // CR-6444: supplier code filter
    supplier_code__icontains: f.supplierCodeFilter || undefined,
    // CR-7350: both resolve the way their columns display, so typing a name
    // the grid plainly shows can never return nothing.
    quality_engineer: f.qualityEngineerFilter || undefined,
    support_quality_engineer: f.supportQualityEngineerFilter || undefined,
    commodity_group: f.commodityGroupFilter || undefined,
    commodity_type: f.commodityTypeFilter || undefined,
    // CR-6444: supplier type filter
    supplier_type: f.supplierTypeFilter || undefined,
    strategic: f.strategicFilter ? true : undefined,
    part_number__icontains: f.partNumberFilter || undefined,
    region: f.regionFilter || undefined,
    liability_type: f.liabilityFilter || undefined,
    warehouse: f.warehouseFilter || undefined,
    claim_status: f.claimStatusFilter || undefined,
    action_status: f.actionStatusFilter || undefined,
    collection_date_from: f.collectionDateFrom || undefined,
    collection_date_to: f.collectionDateTo || undefined,
    // CR-6451
    'supplier__country': f.supplierCountryFilter || undefined,
    search_log_no__icontains: f.srnFilter || undefined,
    ncr_number__icontains: f.ncrIdFilter || undefined,
    is_credit_required: f.creditRequired ? true : undefined,
    is_8d_required: f.eightDRequired ? true : undefined,
    is_collection_required: f.collectionRequired ? true : undefined,
    is_replacement_required: f.replacementRequired ? true : undefined,
  };
}

/**
 * How many panel filters are in force. `defaultDateFrom` is the rolling
 * 12-month window the list loads with — it is the default, not a filter the
 * user chose, so it is not counted.
 */
export function countActiveNCRFilters(f: NCRFiltersState, defaultDateFrom: string): number {
  let n = 0;
  (Object.keys(f) as Array<keyof NCRFiltersState>).forEach((key) => {
    const v = f[key];
    if (key === 'dateFrom' && v === defaultDateFrom) return;
    if (typeof v === 'boolean' ? v : v !== '') n += 1;
  });
  return n;
}
